/**
 * VoicePicker — de merk-stem voor TTS-begroetingen kiezen op basis van
 * beluisterde Nederlandse samples.
 *
 * Toont de stemmen uit het ElevenLabs-account (My Voices). Per stem: NL-sample
 * afspelen (of eerst genereren, wordt gecachet in de bucket) en "Zet als
 * standaard". De standaardstem geldt voor alle nieuwe begroetingen; bestaande
 * audio blijft ongemoeid. Model-toggle (v2/v3) om hetzelfde stemkarakter per
 * model te vergelijken. Data komt via props van de pagina (één GET voor
 * picker + generator samen).
 */

import { useCallback, useRef, useState } from 'react';
import clsx from 'clsx';
import { supabase } from '../../../lib/supabase';

export interface VoiceInfo {
  voiceId: string;
  name: string;
  labels: Record<string, string>;
  previewUrl: string | null;
  samples: Record<string, string | null>;
  isDefault: boolean;
}

export interface VoicesData {
  defaultVoiceId: string;
  defaultModel: string;
  models: string[];
  voices: VoiceInfo[];
}

const MODEL_LABEL: Record<string, string> = {
  eleven_multilingual_v2: 'v2 (bewezen)',
  eleven_v3: 'v3 (expressiever)',
};

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
  return headers;
}

interface Props {
  data: VoicesData | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}

export function VoicePicker({ data, loading, error, onRefresh }: Props) {
  const [model, setModel] = useState<string | null>(null);
  const [busyVoice, setBusyVoice] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const activeModel = model ?? data?.defaultModel ?? 'eleven_multilingual_v2';

  const play = useCallback((url: string) => {
    audioRef.current?.pause();
    audioRef.current = new Audio(url);
    void audioRef.current.play();
  }, []);

  const generateSample = useCallback(
    async (voiceId: string) => {
      setBusyVoice(voiceId);
      setLocalError(null);
      try {
        const r = await fetch('/api/telephony/voices', {
          method: 'POST',
          headers: await authHeaders(),
          body: JSON.stringify({ voiceId, model: activeModel }),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({ error: 'Voorbeeld genereren mislukt' }));
          throw new Error(err.error || 'Voorbeeld genereren mislukt');
        }
        const json = (await r.json()) as { sampleUrl: string };
        play(json.sampleUrl);
        onRefresh();
      } catch (err) {
        setLocalError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusyVoice(null);
      }
    },
    [activeModel, onRefresh, play],
  );

  const setDefault = useCallback(
    async (voiceId: string) => {
      setBusyVoice(voiceId);
      setLocalError(null);
      try {
        const r = await fetch('/api/telephony/voices', {
          method: 'PUT',
          headers: await authHeaders(),
          body: JSON.stringify({ voiceId, model: activeModel }),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({ error: 'Standaard zetten mislukt' }));
          throw new Error(err.error || 'Standaard zetten mislukt');
        }
        onRefresh();
      } catch (err) {
        setLocalError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusyVoice(null);
      }
    },
    [activeModel, onRefresh],
  );

  if (loading) return <div className="h-16 bg-slate-50 rounded animate-pulse" />;
  if (error || !data) {
    return (
      <p className="text-sm text-slate-500">
        Stemmen niet beschikbaar{error ? `: ${error}` : ''}.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {localError && (
        <div className="px-3 py-2 bg-red-50 border border-red-200 text-red-800 rounded text-sm">{localError}</div>
      )}

      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-500">Model</span>
        {data.models.map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setModel(m)}
            className={clsx(
              'px-2 py-1 rounded text-xs font-medium',
              activeModel === m ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200',
            )}
          >
            {MODEL_LABEL[m] ?? m}
          </button>
        ))}
      </div>

      <ul className="divide-y divide-slate-100">
        {data.voices.map((v) => {
          const sampleUrl = v.samples[activeModel] ?? null;
          const isCurrentDefault = v.isDefault && activeModel === data.defaultModel;
          const busy = busyVoice === v.voiceId;
          return (
            <li key={v.voiceId} className="py-2.5 flex flex-wrap items-center gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-800">{v.name}</span>
                  {isCurrentDefault && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-medium">
                      Standaardstem
                    </span>
                  )}
                </div>
                {Object.keys(v.labels).length > 0 && (
                  <div className="text-xs text-slate-400 mt-0.5">
                    {Object.values(v.labels).join(' · ')}
                  </div>
                )}
              </div>
              {sampleUrl ? (
                <button
                  type="button"
                  onClick={() => play(sampleUrl)}
                  className="px-2.5 py-1 rounded text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200"
                >
                  Beluister NL
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => void generateSample(v.voiceId)}
                  disabled={busy}
                  className={clsx(
                    'px-2.5 py-1 rounded text-xs font-medium',
                    busy ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-slate-100 text-slate-600 hover:bg-slate-200',
                  )}
                >
                  {busy ? 'Genereren…' : 'Genereer voorbeeld'}
                </button>
              )}
              {!isCurrentDefault && (
                <button
                  type="button"
                  onClick={() => void setDefault(v.voiceId)}
                  disabled={busy}
                  className={clsx(
                    'px-2.5 py-1 rounded text-xs font-medium',
                    busy ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-700',
                  )}
                >
                  Zet als standaard
                </button>
              )}
            </li>
          );
        })}
        {data.voices.length === 0 && (
          <li className="py-2 text-sm text-slate-500">
            Geen stemmen in het ElevenLabs-account. Voeg kandidaten toe via My Voices in de ElevenLabs-UI.
          </li>
        )}
      </ul>
      <p className="text-xs text-slate-400">
        De voorbeeldzin is voor elke stem gelijk. De standaardstem geldt voor nieuwe begroetingen;
        bestaande begroetingen behouden hun audio tot je ze opnieuw genereert.
      </p>
    </div>
  );
}
